import type { ClassData, Student, StreamPost, Submission } from '../types';

/**
 * Temporarily replace student names with pseudonyms, for projecting a class list.
 *
 * A teacher going over marks on the board wants the grid, not the roster. Every
 * other approach to this problem is a per-view toggle that misses a view — and a
 * mask that misses one view is WORSE than no mask, because the teacher has been
 * told the names are hidden and is now projecting with confidence.
 *
 * So the names are replaced in the DATA handed to the views, not at 224 separate
 * render sites. Every screen that renders a student — grid, attendance, seating,
 * flashcards, reports, XP — shows the pseudonym without knowing this feature
 * exists, and a view added next year is covered on the day it is written.
 *
 * THE DANGER OF DOING IT THAT WAY, AND THE GUARD.
 * Several writers build a whole `students` array from what is on screen and save
 * it. If one ran while masked, it would persist the pseudonyms over the real
 * roster — every name in the class, destroyed, silently. So every masked student
 * carries `nameMasked: true`, and `updateClass` REFUSES to write a roster that
 * contains it. Masking is a display state; a refusal is loud, recoverable and
 * impossible to miss, which is what that trade needs to be.
 *
 * Not persisted anywhere. It lives in session state and is gone on reload, so a
 * mask can never be left on by accident and then mistaken for the real roster.
 */

/**
 * The pseudonym pool.
 *
 * Animals rather than "Student 7": a class can follow a row across a projected
 * grid by name, and a child reading the board cannot decode who "Otter" is the way
 * they can decode initials or an alphabetical "Student 7". Deliberately no
 * adjectives — "Swift Otter" wraps in a narrow grid column.
 */
const POOL = [
  'Otter', 'Falcon', 'Badger', 'Heron', 'Marten', 'Osprey', 'Lynx', 'Puffin',
  'Beaver', 'Kestrel', 'Raven', 'Hare', 'Wren', 'Moose', 'Owl', 'Fox',
  'Bison', 'Loon', 'Elk', 'Seal', 'Crane', 'Mink', 'Swan', 'Stoat',
  'Caribou', 'Grouse', 'Salmon', 'Shrew', 'Vole', 'Weasel', 'Coyote', 'Sparrow',
] as const;

/** A stable non-cryptographic hash, so the same child keeps the same pseudonym. */
function hash(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

/** Marker for a student object whose name has been replaced for display. */
export interface MaskedStudent extends Student {
  nameMasked: true;
}

export const isMaskedStudent = (s: unknown): boolean =>
  !!s && typeof s === 'object' && (s as { nameMasked?: unknown }).nameMasked === true;

/** Does this roster contain any masked student? Used by the write guard. */
export const rosterIsMasked = (students: unknown): boolean =>
  Array.isArray(students) && students.some(isMaskedStudent);

/**
 * Pseudonyms for one roster.
 *
 * Derived from the student id so a child keeps the same pseudonym for as long as
 * the mask is on — a teacher comparing two columns needs the row to stay put.
 * Collisions are resolved by walking the pool, then by numbering, so two children
 * are never both "Otter"; without that, a projected grid would have two identical
 * rows and the teacher would read the wrong one.
 */
export function maskStudents(students: Student[] | undefined | null): MaskedStudent[] {
  const list = Array.isArray(students) ? students : [];
  const taken = new Set<string>();
  return list.map((s, index) => {
    const start = hash(s.id || String(index)) % POOL.length;
    let name = '';
    for (let step = 0; step < POOL.length; step++) {
      const candidate = POOL[(start + step) % POOL.length];
      if (!taken.has(candidate)) { name = candidate; break; }
    }
    if (!name) {
      // More students than the pool: number them rather than repeat.
      let n = 2;
      while (taken.has(`${POOL[start]} ${n}`)) n++;
      name = `${POOL[start]} ${n}`;
    }
    taken.add(name);
    return {
      ...s,
      firstName: name,
      lastName: '',
      // Every other field that carries a real name has to go too, or a view that
      // prefers one of them quietly un-masks the child.
      preferredName: undefined,
      realFirstName: undefined,
      realLastName: undefined,
      email: undefined,
      parentName: undefined,
      parentEmail: undefined,
      nameMasked: true,
    } as MaskedStudent;
  });
}

/**
 * Mask a whole class, not just its `students` array.
 *
 * Masking the roster alone leaves the child's name in three other places a teacher
 * can reach in one click, all of them found by review rather than by me:
 *
 *  1. DENORMALISED NAMES. Every Hub post, reply and submission stores the author's
 *     name as text at write time (`authorName`, `studentName`, `groupMemberNames`).
 *     Those are not the roster, so masking the roster did nothing to them and the
 *     Class Hub tab showed real names under a mask the teacher trusted.
 *  2. `studentPrivate`. Guardian name, email, phone and the OEN are hydrated onto
 *     the class separately, and StudentDetailView renders them — so a pseudonymous
 *     row opened to a card with the child's surname on it.
 *  3. Nothing else: grade keys are ids, and ids are deliberately left alone so the
 *     views still work.
 *
 * Names are rewritten by ID where an id exists, and by exact name match otherwise,
 * because that is the only handle a reply carries.
 */
export function maskClassData<T extends ClassData>(cls: T): T {
  const masked = maskStudents(cls.students);
  const byId = new Map<string, string>();
  const byName = new Map<string, string>();
  (cls.students || []).forEach((real, i) => {
    const pseudo = masked[i]?.firstName || 'Student';
    if (real.id) byId.set(real.id, pseudo);
    // Every spelling a denormalised field might have stored.
    for (const form of [
      `${real.firstName} ${real.lastName}`.trim(),
      real.firstName,
      real.preferredName,
      real.realFirstName && `${real.realFirstName} ${real.realLastName || ''}`.trim(),
      real.realFirstName,
    ]) {
      if (form) byName.set(form.trim().toLowerCase(), pseudo);
    }
  });
  const rename = (id: string | undefined, name: string | undefined): string | undefined => {
    if (id && byId.has(id)) return byId.get(id);
    if (name && byName.has(name.trim().toLowerCase())) return byName.get(name.trim().toLowerCase());
    return name;
  };

  const stream: StreamPost[] | undefined = cls.stream?.map(post => ({
    ...post,
    authorName: post.role === 'student' ? rename(post.authorId, post.authorName) : post.authorName,
    replies: post.replies?.map(r => ({
      ...r,
      authorName: (r.role === 'student' ? rename(undefined, r.authorName) : r.authorName) || r.authorName,
    })),
  }));

  const submissions: Submission[] | undefined = cls.submissions?.map(sub => ({
    ...sub,
    studentName: rename(sub.studentId, sub.studentName) || sub.studentName,
    groupMemberNames: sub.groupMemberNames?.map((n, i) =>
      rename(sub.groupMemberIds?.[i], n) || n),
    // The teacher-student thread carries the child's name on every message.
    thread: (sub as { thread?: Array<{ authorRole?: string; authorName?: string }> }).thread?.map(m => ({
      ...m,
      authorName: m.authorRole === 'student' ? (rename(undefined, m.authorName) || m.authorName) : m.authorName,
    })),
  })) as Submission[] | undefined;

  return {
    ...cls,
    students: masked,
    // Guardian contact and the OEN are hydrated here, outside `students`.
    studentPrivate: undefined,
    ...(stream ? { stream } : {}),
    ...(submissions ? { submissions } : {}),
  } as T;
}
